import { makeGetMenu } from 'main/factories/usecases/menu/remote-get-menu-factory';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMenu } from '../hooks/useMenu';
import { useAppSelector } from '../hooks/useRedux';

type MenuItem = {
  id: string;
  title: string;
  icon: string;
  route?: string;
  children?: MenuItem[];
  parent?: MenuItem;
};

const Navdata = () => {
  const [activeMenuIds, setActiveMenuIds] = useState<any[]>([]);

  const { menu } = useAppSelector((state) => state.menu);
  const getMenu = makeGetMenu();

  const { getMenuItems } = useMenu({
    getMenu,
  });

  const formatMenu = (menu: MenuItem[]): MenuItem[] => {
    const formatItem = (
      item: MenuItem,
      parent: MenuItem | null = null,
    ): MenuItem => {
      return {
        ...item,
        parent: parent,
        children: item.children
          ? item.children.map((child) => formatItem(child, item))
          : [],
      };
    };

    return menu.map((item) => formatItem(item));
  };

  const findMatchingMenuItem = (
    menu: MenuItem[],
    pathName: string,
    parent: MenuItem | null = null,
  ): MenuItem | null => {
    for (const item of menu) {
      if (`/${item.route}` === pathName) {
        return { ...item, parent };
      }
      if (item.children) {
        const matchingChild = findMatchingMenuItem(
          item.children,
          pathName,
          item,
        );
        if (matchingChild) {
          return { ...matchingChild, parent: item };
        }
      }
    }
    return null;
  };

  const findParentChain = (menu: MenuItem[], target: MenuItem): MenuItem[] => {
    let chain: MenuItem[] = [];
    const helper = (
      menu: MenuItem[],
      target: MenuItem,
      currentChain: MenuItem[],
    ): boolean => {
      for (let menuItem of menu) {
        if (menuItem.id === target.id) {
          chain = currentChain.concat(menuItem);
          return true;
        }
        if (
          menuItem.children &&
          helper(menuItem.children, target, currentChain.concat(menuItem))
        ) {
          return true;
        }
      }
      return false;
    };
    helper(menu, target, []);
    return chain;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const initMenu = () => {
      const path = window.location.pathname;
      const pathName = process.env.PUBLIC_URL + path;

      const matchingMenuItem = findMatchingMenuItem(formatMenu(menu), pathName);

      if (matchingMenuItem) {
        const parentChain = findParentChain(formatMenu(menu), matchingMenuItem);

        setActiveMenuIds((prevActiveMenuIds: any) => {
          let newActiveMenuIds = { ...prevActiveMenuIds };

          parentChain.forEach((item) => {
            newActiveMenuIds[item.id] = true;
          });

          return newActiveMenuIds;
        });
      }
    };

    initMenu();
  }, [menu]);

  useEffect(() => {
    getMenuItems();
  }, []);

  function updateIconSidebar(e: any) {
    if (e && e.target && e.target.getAttribute('sub-items')) {
      const ul: any = document.getElementById('two-column-menu');
      const iconItems: any = ul.querySelectorAll('.nav-icon.active');
      const activeIconItems = [...iconItems];
      activeIconItems.forEach((item) => {
        item.classList.remove('active');
        const id = item.getAttribute('sub-items');
        const getID = document.getElementById(id) as HTMLElement;
        if (getID) getID.classList.remove('show');
      });
    }
  }

  const navigate = useNavigate();

  const handleMenuItemClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: MenuItem,
  ) => {
    e.preventDefault();
    if (item.route) {
      navigate('/' + item.route);
    }
    updateIconSidebar(e);

    const findAllMenuIds = (menu: MenuItem[]): string[] => {
      let ids: string[] = [];
      menu.forEach((menuItem) => {
        ids.push(menuItem.id);
        if (menuItem.children) {
          ids = ids.concat(findAllMenuIds(menuItem.children));
        }
      });
      return ids;
    };

    const findParentChain = (menu: MenuItem[], target: MenuItem): string[] => {
      let chain: string[] = [];
      const helper = (
        menu: MenuItem[],
        target: MenuItem,
        currentChain: string[],
      ): boolean => {
        for (let menuItem of menu) {
          if (menuItem.id === target.id) {
            chain = currentChain.concat(menuItem.id);
            return true;
          }
          if (
            menuItem.children &&
            helper(menuItem.children, target, currentChain.concat(menuItem.id))
          ) {
            return true;
          }
        }
        return false;
      };
      helper(menu, target, []);
      return chain;
    };

    const allMenuIds = findAllMenuIds(menu);
    const parentChain = findParentChain(menu, item);

    setActiveMenuIds((prevActiveMenuIds: any) => {
      let newActiveMenuIds = { ...prevActiveMenuIds };

      allMenuIds.forEach((id) => {
        if (!parentChain.includes(id)) {
          newActiveMenuIds[id] = false;
        }
      });

      newActiveMenuIds[item.id] = !prevActiveMenuIds[item.id];

      return newActiveMenuIds;
    });
  };

  const mapMenuItems = (items: MenuItem[], parent?: MenuItem): any[] => {
    return items.map((item) => {
      const isActive = !!activeMenuIds[item.id];

      const mappedItem = {
        id: item.id,
        label: item.title,
        icon: item.icon,
        stateVariables: isActive,
        link: item.route ? `/${item.route}` : '',
        click: (e: any) => handleMenuItemClick(e, { ...item, parent: parent }),
        subItems: item.children ? mapMenuItems(item.children, item) : [],
        parent: parent,
      };

      if (item.children) {
        mappedItem.subItems = mapMenuItems(item.children, item);
      }

      return mappedItem;
    });
  };

  const menuItems = () => mapMenuItems(formatMenu(menu));

  return menuItems();
};

export default Navdata;
